import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { UserPlus, Settings, Rocket } from 'lucide-react';

interface Step {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  time: string;
  gradient: string;
  border: string;
  numberBg: string;
  numberBorder: string;
  numberText: string;
  timeBg: string;
  timeText: string;
}

interface SeoGettingStartedSectionProps {
  title?: string;
  subtitle?: string;
  steps?: Step[];
}

const SeoGettingStartedSection = ({ 
  title = "Get started in 3 simple steps",
  subtitle = "From setup to your first professional blog in under 10 minutes",
  steps: customSteps
}: SeoGettingStartedSectionProps) => {
  const reduce = useReducedMotion();

  const defaultSteps = [
    {
      icon: UserPlus,
      title: "Set up your Brand Vault",
      description: "Define your tone, audience, and rules once—every blog draft will follow automatically.",
      time: "2 minutes",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
      border: "border-blue-200/50 dark:border-blue-800/50",
      numberBg: "bg-blue-100 dark:bg-blue-900/50",
      numberBorder: "border-blue-300/50 dark:border-blue-700/50",
      numberText: "text-blue-700 dark:text-blue-300",
      timeBg: "bg-blue-600 dark:bg-blue-500",
      timeText: "text-white"
    },
    {
      icon: Settings,
      title: "Plan your calendar",
      description: "Use the Blog Calendar Agent to schedule topics and seasonal ideas, so you never run out of material.",
      time: "3 minutes",
      gradient: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
      border: "border-emerald-200/50 dark:border-emerald-800/50",
      numberBg: "bg-emerald-100 dark:bg-emerald-900/50",
      numberBorder: "border-emerald-300/50 dark:border-emerald-700/50",
      numberText: "text-emerald-700 dark:text-emerald-300",
      timeBg: "bg-emerald-600 dark:bg-emerald-500",
      timeText: "text-white"
    },
    {
      icon: Rocket,
      title: "Create and publish",
      description: "Generate outlines with Magic Prompt, draft in Studio, and export straight to your CMS or publishing tools.",
      time: "5 minutes",
      gradient: "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30",
      border: "border-orange-200/50 dark:border-orange-800/50",
      numberBg: "bg-orange-100 dark:bg-orange-900/50",
      numberBorder: "border-orange-300/50 dark:border-orange-700/50",
      numberText: "text-orange-700 dark:text-orange-300",
      timeBg: "bg-orange-600 dark:bg-orange-500",
      timeText: "text-white"
    }
  ];

  const steps = customSteps || defaultSteps;

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-background relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <motion.h2 
            initial={reduce ? undefined : { opacity: 0, y: 20 }}
            whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6"
          >
            {title}
          </motion.h2>
          <motion.p 
            initial={reduce ? undefined : { opacity: 0, y: 20 }}
            whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto"
          >
            {subtitle}
          </motion.p>
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Progressive connecting line - Hidden on mobile */}
          <div className="hidden md:block absolute top-20 left-1/2 transform -translate-x-1/2 w-full max-w-4xl">
            <motion.div
              initial={reduce ? undefined : { scaleX: 0 }}
              whileInView={reduce ? undefined : { scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="h-0.5 bg-gradient-primary origin-left"
            />
          </div>

          {/* Mobile: Vertical connecting lines */}
          <div className="md:hidden absolute left-8 top-20 bottom-20 w-0.5 bg-gradient-to-b from-blue-500/50 via-emerald-500/50 to-orange-500/50" />

          {/* Steps with progressive stacking animation */}
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3 relative">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Background card that builds up */}
                <motion.div
                  initial={reduce ? undefined : { opacity: 0, y: 30, rotateY: -15 }}
                  whileInView={reduce ? undefined : { opacity: 1, y: 0, rotateY: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.3 }}
                  className={`${step.gradient} ${step.border} backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 h-full transform-gpu shadow-sm touch-manipulation`}
                  style={{ transformStyle: 'preserve-3d' }}
                >

                  {/* Step content builds progressively */}
                  <div className="relative z-10">
                    {/* Animated step number - Touch-friendly size */}
                    <motion.div
                      initial={reduce ? undefined : { scale: 0, rotate: -180 }}
                      whileInView={reduce ? undefined : { scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.3 + 0.1, type: "spring", stiffness: 200 }}
                      className={`flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full ${step.numberBg} border-2 ${step.numberBorder} mb-4 sm:mb-6 mx-auto`}
                    >
                      <span className={`text-xl sm:text-2xl font-bold ${step.numberText}`}>
                        {index + 1}
                      </span>
                    </motion.div>

                    {/* Time badge slides in */}
                    <motion.div
                      initial={reduce ? undefined : { x: -20, opacity: 0 }}
                      whileInView={reduce ? undefined : { x: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.3 + 0.4 }}
                      className="flex justify-center mb-3 sm:mb-4"
                    >
                      <div className={`px-3 sm:px-4 py-1.5 sm:py-2 ${step.timeBg} ${step.timeText} text-xs sm:text-sm font-semibold rounded-full shadow-sm`}>
                        {step.time}
                      </div>
                    </motion.div>

                    {/* Title builds up */}
                    <motion.h3
                      initial={reduce ? undefined : { opacity: 0, y: 10 }}
                      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.3 + 0.5 }}
                      className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-center"
                    >
                      {step.title}
                    </motion.h3>

                    {/* Description appears last */}
                    <motion.p
                      initial={reduce ? undefined : { opacity: 0, y: 10 }}
                      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.3 + 0.6 }}
                      className="text-sm sm:text-base text-muted-foreground text-center leading-relaxed"
                    >
                      {step.description}
                    </motion.p>

                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoGettingStartedSection;